import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api/client';
import PanelCard, { EmptyState } from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

/**
 * Staff → Invite codes (admin és owner) — az utolsó 30 meghívó-kód (Discord-bot /invite; 1 óra, egyszer használható):
 * kinek (a Codex-fiók és, ha már regisztrált, az app-neve), ki adta, mikor, és mi lett vele. 2026-10-08 — a tulajdonos:
 * „az invite codes-ot át kéne tenni másik al fülre mert most a codes és az accounts is egyben van”.
 * A szerver: bot-backend controllers/appAdmin.controller.js listInvites.
 */

const STATUS = {
  active:  { label: 'Active',  cls: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25' },
  used:    { label: 'Used',    cls: 'bg-white/[0.05] text-gray-300 border-white/[0.08]' },
  expired: { label: 'Expired', cls: 'bg-white/[0.05] text-gray-500 border-white/[0.08]' },
};

// Az időpontok UTC-ben jönnek; a staff helyi idejében mutatjuk.
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');

export default function InvitesTab() {
  const [invites, setInvites] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/app-invites');
      setInvites(res.data.invites);
    } catch {
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PanelCard
      title="Invite codes"
      subtitle="The last 30 codes made with /invite on Discord (valid for one hour, single use)."
      actions={<button type="button" className="btn-glass btn-sm" onClick={load} disabled={loading}>Refresh</button>}
      flush
    >
      {invites === null ? (
        <div className="py-8 flex justify-center"><LoadingSpinner /></div>
      ) : invites.length === 0 ? (
        <EmptyState title="No invite codes yet." hint="Codes appear here once staff runs /invite in a ticket." />
      ) : (
        <ul className={`divide-y divide-line-faint ${loading ? 'opacity-60' : ''}`}>
          {invites.map(i => {
            const s = STATUS[i.status] || STATUS.expired;
            return (
              <li key={i.id} className="px-4 sm:px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px]">
                <span className="min-w-[200px] flex-1 text-gray-300">
                  For <b className="text-gray-50">{i.codexUsername}</b>
                  {i.appUsername ? <span className="text-gray-500"> (app: {i.appUsername})</span> : null}
                  <span className="text-gray-500"> · by {i.createdBy || '—'}</span>
                </span>
                <span className="text-gray-500">
                  {when(i.createdAt)}
                  {i.usedAt ? ` · used ${when(i.usedAt)}` : ''}
                </span>
                <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-medium ${s.cls}`}>{s.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </PanelCard>
  );
}
